import type { ElementType } from "react";
import { Dropdown } from "react-bootstrap";

import type { DashboardListItem } from "src/types/dashboard";

interface BuilderDashboardNavDropdownProps {
  dropdownKey: string;
  label: string;
  dashboards: DashboardListItem[];
  selectedDashboardCode: string | null;
  toggleAs: ElementType;
  openKey: string | null;
  onOpen: (key: string) => void;
  onScheduleClose: (key: string) => void;
  onSetOpenKey: (key: string | null) => void;
  onNavigate: (code: string) => void;
  onSetDefault: (code: string) => void;
  /** Shown instead of an empty menu when `dashboards` is empty. Only "My
   *  Dashboards" passes this, matching the original inline behavior. */
  emptyMessage?: string;
}

/**
 * One nav dropdown listing dashboards, with a star toggle to set the
 * default — the builder header's version of DashboardNavDropdown. Kept as
 * its own component rather than shared with the analytics header's: this
 * one uses a plain-button toggle (not Nav.Link) and skips renderOnMount, so
 * unifying them would need extra prop plumbing for no real benefit.
 */
export default function BuilderDashboardNavDropdown({
  dropdownKey,
  label,
  dashboards,
  selectedDashboardCode,
  toggleAs,
  openKey,
  onOpen,
  onScheduleClose,
  onSetOpenKey,
  onNavigate,
  onSetDefault,
  emptyMessage,
}: BuilderDashboardNavDropdownProps) {
  const hasActiveDashboard = dashboards.some(
    (dashboard) => selectedDashboardCode === dashboard.code,
  );

  return (
    <Dropdown
      as="li"
      className="nav-item"
      show={openKey === dropdownKey}
      onMouseEnter={() => onOpen(dropdownKey)}
      onMouseLeave={() => onScheduleClose(dropdownKey)}
      onToggle={(nextShow) => onSetOpenKey(nextShow ? dropdownKey : null)}
    >
      <Dropdown.Toggle
        as={toggleAs}
        id={dropdownKey}
        className={`nav-link ${hasActiveDashboard ? " custom-active-link" : ""}`}
      >
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu
        onMouseEnter={() => onOpen(dropdownKey)}
        onMouseLeave={() => onScheduleClose(dropdownKey)}
        popperConfig={{
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, 12] },
            },
          ],
        }}
      >
        {dashboards.length > 0
          ? dashboards.map((dashboard) => (
              <Dropdown.Item
                key={dashboard.code}
                as="button"
                active={selectedDashboardCode === dashboard.code}
                className="d-flex align-items-center justify-content-between"
                onClick={() => onNavigate(dashboard.code)}
              >
                {dashboard.name}
                <i
                  className={`bi ms-2 ${dashboard.is_default ? "bi-star-fill text-warning" : "bi-star"}`}
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!dashboard.is_default) onSetDefault(dashboard.code);
                  }}
                />
              </Dropdown.Item>
            ))
          : emptyMessage && (
              <Dropdown.Item disabled>{emptyMessage}</Dropdown.Item>
            )}
      </Dropdown.Menu>
    </Dropdown>
  );
}
